// Motor do assistente V2 — árvore de intenções guiada por regras (sem IA, sem
// backend). Cada intenção devolve mensagens do bot com "chips" (próximas
// perguntas) e "actions" (navegar, destacar um elemento na tela, abrir chamado).
// Textos revisados com o time GCA (documento "FAQ e assistente V3", 29/09/2026, e
// "Compilado FAC", 13/10/2026):
// o canal de contato é o chamado da área da conta — sem WhatsApp nem 0800 aqui.
// Nada é inventado sobre prazo ou status.
import { orderStatus } from '@utils/orderStatus'

export const TICKET_PATH = '/v2/conta/atendimento'
export const EXCHANGE_EMAIL = 'smdireto@grupo-sm.com'
// formulário público de atendimento (o mesmo do "Fale Conosco" do V1) — só para quem
// não consegue entrar na conta; com conta, o canal é o chamado da área logada
export const PUBLIC_FORM_URL = 'https://app.pipefy.com/public/form/4k5tgRPD'

export const normalize = s =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

// ---------------------------------------------------------------------------
// helpers de mensagem
const msg = (text, extra = {}) => ({ from: 'bot', text, ...extra })
const chip = (label, intent) => ({ label, intent })
const go = (label, to) => ({ label, kind: 'navigate', to })
const spot = (label, selector, path) => ({ label, kind: 'spotlight', selector, path })
// abre o chamado da conta (passa pelo login quando não há sessão)
const ticket = (label = 'Abrir um chamado') => ({ label, kind: 'ticket' })
const publicForm = () => ({ label: 'Abrir o formulário de atendimento', kind: 'link', href: PUBLIC_FORM_URL })

// ordem da jornada do cliente (Compilado FAC, 13/10)
export const MAIN_CHIPS = [
  chip('Onde digito o voucher?', 'onde_voucher'),
  chip('Voucher não funciona', 'voucher'),
  chip('Senha e cadastro', 'cadastro'),
  chip('Formas de pagamento', 'pagamento'),
  chip('Nota fiscal', 'nota'),
  chip('Prazo de entrega', 'prazo'),
  chip('Onde está meu pedido?', 'status'),
  chip('Cancelamento ou troca', 'cancelamento'),
  chip('Abrir um chamado', 'humano'),
]

const BACK = chip('Outro assunto', 'menu')
const HUMAN = chip('Abrir um chamado', 'humano')

const EXCHANGE_RULE =
  'A troca se aplica quando o produto for entregue com avaria, com erro operacional ou de gráfica. Você tem até 30 dias corridos, após a data de recebimento, para reportar.'
const EXCHANGE_HOW = `Para solicitar a troca, envie um e-mail para ${EXCHANGE_EMAIL}. Informe o número do pedido e o título recebido.`

// ---------------------------------------------------------------------------
// leitura do pedido ativo (detalhe) — mesma derivação da tela V2OrderDetail
const describeActiveOrder = (order, ctx) => {
  const subs = Object.values(order.sub_orders || {}).flat()
  const digital = subs.filter(s => s.business_line_id === 7)
  const home = subs.filter(s => s.business_line_id !== 7 && !!s.school_is_b2c)
  const school = subs.filter(s => s.business_line_id !== 7 && !s.school_is_b2c)
  const withTracking = home.filter(s => (s.history || []).some(h => h.status === 'PT') && s.tracking_link)
  // modelo de entrega imediata: o contrato da escola marca o pedido (mesmo campo da tela)
  const immediate = String(order.immediate_delivery) === '1'
  const code = typeof order.order_status === 'string' ? order.order_status : typeof order.status === 'string' ? order.status : 'null'
  const st = orderStatus[code] || orderStatus['null']

  const parts = [`Você está vendo o pedido SMD-${order.order_id}, status "${st.description}".`]
  const actions = []
  if (school.length && immediate) parts.push('A entrega deste pedido é imediata: você retira os livros na escola logo após a compra, no prazo que a escola informar.')
  if (school.length && !immediate) parts.push('Os livros deste pedido são entregues na escola, que informa a data de retirada. Não há rastreio individual nesse caso.')
  if (home.length && !withTracking.length) parts.push('A entrega é na sua casa. O rastreio fica disponível aqui no detalhe assim que o pedido sair com a transportadora.')
  if (withTracking.length) {
    parts.push('A entrega é na sua casa e o rastreio já está disponível. Posso destacar o link na tela.')
    actions.push(spot('Mostrar o rastreio', `#v2-order-track-${withTracking[0].id}`, ctx.pathname))
  }
  if (digital.length) parts.push('Há licença digital neste pedido: após a confirmação do pagamento ela é enviada ao e-mail da compra.')
  if (code === 'PW') actions.push(spot('Ver chave PIX', '#v2-order-pixkey', ctx.pathname))
  if ((code === 'NP' || code === 'BAP') && order.billet_url) actions.push(spot('2ª via do boleto', '#v2-order-billet2', ctx.pathname))
  return msg(parts.join(' '), { actions, chips: [chip('Prazo de entrega', 'prazo'), HUMAN, BACK] })
}

const statusHint = code => {
  switch (code) {
    case 'PW':
    case 'BAP':
      return 'Seu pedido mais recente ainda aguarda pagamento. Boleto: a 2ª via fica no botão do pedido. PIX: abra o detalhe e use "Ver chave PIX".'
    case 'NP':
    case 'null':
    case 'DV':
      return 'O pagamento do pedido mais recente não foi aprovado. Confira os dados com a operadora do cartão e tente novamente. Se o problema continuar, abra um chamado.'
    case 'PT':
    case 'PET':
      return 'Seu pedido mais recente já está com a transportadora. Em "Ver detalhes" do pedido há o link para acompanhar a entrega.'
    case 'PE':
      return 'Seu pedido mais recente consta como entregue. Se algum livro chegou com avaria, erro operacional ou de gráfica, você tem até 30 dias corridos após o recebimento para pedir a troca.'
    case 'C':
    case 'CM':
    case 'CBS':
    case 'CES':
      return 'Seu pedido mais recente está cancelado. Se não foi você quem cancelou, abra um chamado.'
    default:
      return 'Pagamento confirmado: o pedido está em preparação. Entrega imediata ou na escola: a escola informa a data de retirada. Entrega na sua casa: o rastreio fica disponível em "Ver detalhes" do pedido.'
  }
}

// ---------------------------------------------------------------------------
export const INTENTS = {
  menu: {
    keywords: ['menu', 'inicio', 'voltar', 'ajuda', 'opcoes'],
    respond: () => [msg('Sobre o que você quer falar?', { chips: MAIN_CHIPS })],
  },

  status: {
    keywords: ['pedido', 'status', 'onde esta', 'rastre', 'rastrear', 'chegou', 'chegar', 'transportadora', 'correio', 'andamento', 'acompanhar', 'entrega', 'entregue', 'enviado', 'compra'],
    respond: async ctx => {
      if (!ctx.authenticated) {
        return [
          msg('Para consultar o status, você precisa entrar na sua conta. Depois de entrar, te levo direto para a lista de pedidos.', {
            actions: [go('Entrar na minha conta', '/v2/conta/entrar')],
            chips: [chip('Prazo de entrega', 'prazo'), BACK],
          }),
        ]
      }
      const active = ctx.activeOrder
      if (active && ctx.pathname === `/v2/conta/pedidos/${active.order_id}`) return [describeActiveOrder(active, ctx)]

      let data
      try {
        data = await ctx.fetchOrders()
      } catch (e) {
        return [msg('Não consegui consultar seus pedidos agora. Tente pela lista de pedidos ou abra um chamado.', { actions: [go('Meus pedidos', '/v2/conta/pedidos'), ticket()], chips: [BACK] })]
      }
      const orders = (data && data.orders) || []
      if (!orders.length) {
        return [msg('Não encontrei pedidos na sua conta. Se você comprou com outro e-mail, entre com ele. Se acabou de pagar, aguarde alguns minutos e consulte de novo.', { actions: [go('Meus pedidos', '/v2/conta/pedidos')], chips: [HUMAN, BACK] })]
      }
      const first = orders[0]
      const code = String(first.order_status ?? first.status)
      return [
        msg(orders.length === 1 ? 'Encontrei este pedido na sua conta:' : `Encontrei ${orders.length > 3 ? 'seus pedidos mais recentes' : 'estes pedidos'} na sua conta:`, {
          orders: orders.slice(0, 3).map(o => ({
            id: o.order_id,
            created: o.created,
            total: o.total,
            code: o.order_status ?? o.status,
            status: orderStatus[o.order_status ?? o.status] || orderStatus['null'],
          })),
        }),
        msg(statusHint(code), {
          actions: orders.length > 3 ? [go('Ver todos os pedidos', '/v2/conta/pedidos')] : [],
          chips: [chip('Prazo de entrega', 'prazo'), chip('Nota fiscal', 'nota'), HUMAN, BACK],
        }),
      ]
    },
  },

  prazo: {
    keywords: ['prazo', 'quando chega', 'demora', 'dias', 'data de entrega', 'retirada', 'retirar', 'quando recebo'],
    respond: ctx => [
      msg(
        'A entrega é definida pela sua escola. Veja como funciona em cada modelo:\n' +
          '• Entrega imediata: você retira os livros na escola logo após a compra, no prazo que a escola informar.\n' +
          '• Entrega na escola: os livros são enviados para a escola, que informa a data de retirada.\n' +
          '• Entrega na sua casa: a estimativa de prazo aparece no resumo, antes do pagamento. Depois do envio, o rastreio fica disponível em "Ver detalhes" do pedido.',
        {
          chips: [chip(ctx.authenticated ? 'Ver meus pedidos' : 'Onde está meu pedido?', 'status'), chip('Posso retirar na SM?', 'retirada'), BACK],
        }
      ),
    ],
  },

  retirada: {
    keywords: ['retirar na sm', 'buscar na sm', 'ir na sm'],
    respond: () => [
      msg('Não. Os livros não são retirados na SM: a entrega segue o modelo definido pela sua escola (imediata, na escola ou na sua casa).', {
        chips: [chip('Prazo de entrega', 'prazo'), BACK],
      }),
    ],
  },

  onde_voucher: {
    keywords: ['onde digito', 'onde coloco', 'onde insiro', 'onde informo', 'onde ponho', 'digitar o voucher', 'campo do voucher'],
    respond: ctx => [
      msg('O voucher vai no campo "Número do voucher", no topo da página inicial. Digite o código como veio da escola e clique em "Próximo". Eu mostro onde fica.', {
        actions: [spot('Mostrar onde digitar', '#v2-voucher-input', ctx.pathname === '/' ? '/' : '/v2')],
        chips: [chip('Voucher não funciona', 'voucher'), BACK],
      }),
    ],
  },

  disponibilidade: {
    keywords: ['ate quando', 'quando posso comprar', 'julho', 'agosto', 'fechado', 'indisponivel', 'amazon', 'agrega', 'ano letivo', 'proximo ano', 'fora de epoca'],
    respond: () => [
      msg('O portal para livros didáticos permanece disponível até julho. Após esse período, os materiais poderão estar disponíveis em parceiros como Amazon e Agrega. Em agosto, o portal entra em preparação para o próximo ano letivo e as vendas ficam indisponíveis. Solicitações de Sistemas de Ensino devem ser feitas por chamado.', {
        actions: [ticket()],
        chips: [BACK],
      }),
    ],
  },

  voucher: {
    keywords: ['voucher', 'codigo', 'cupom', 'invalido', 'expirou', 'vencido', 'vencida', 'nao funciona', 'utilizado', 'ja foi usado', 'nao aceita', 'nao reconhece'],
    respond: () => [
      msg('O que o portal mostrou quando você digitou o voucher?', {
        chips: [chip('Diz que é inválido', 'voucher_invalido'), chip('Fora do prazo / expirou', 'voucher_prazo'), chip('Já foi utilizado', 'voucher_usado'), chip('Não tenho voucher', 'voucher_sem'), BACK],
      }),
    ],
  },
  voucher_invalido: {
    keywords: [],
    respond: ctx => [
      msg('Confira se digitou exatamente como veio da escola: letras e números, sem espaços. O voucher é individual por aluno e escola, então o de um irmão não vale para o outro. Se preferir, eu mostro onde digitar.', {
        actions: [spot('Mostrar onde digitar', '#v2-voucher-input', ctx.pathname === '/' ? '/' : '/v2')],
        chips: [chip('Continua inválido', 'humano'), BACK],
      }),
    ],
  },
  voucher_prazo: {
    keywords: [],
    respond: () => [
      msg('Cada voucher tem um período de compra definido pela escola. Fora desse período o portal não aceita a compra. Confirme com a escola se o prazo foi encerrado ou se haverá uma nova janela de compra.', {
        chips: [chip('Até quando posso comprar?', 'disponibilidade'), HUMAN, BACK],
      }),
    ],
  },
  voucher_usado: {
    keywords: [],
    respond: () => [
      msg('Cada voucher permite uma compra. Se ele consta como utilizado e você não comprou, abra um chamado informando o número do voucher. Nunca envie senha ou dados de cartão.', {
        actions: [ticket()],
        chips: [chip('Ver meus pedidos', 'status'), BACK],
      }),
    ],
  },
  voucher_sem: {
    keywords: ['nao tenho voucher', 'sem voucher', 'nao recebi'],
    respond: () => [
      msg('O voucher é fornecido pela escola do aluno. Peça-o à secretaria da escola. A compra com desconto é exclusiva para pais e/ou responsáveis de alunos de escolas cadastradas na SM.', {
        chips: [HUMAN, BACK],
      }),
    ],
  },

  pagamento: {
    keywords: ['pagamento', 'pagar', 'pix', 'boleto', 'cartao', 'parcel', 'recusad', 'segunda via', '2 via', 'juros', 'credito', 'debito', 'dois cartoes', 'aprovado'],
    respond: () => [
      msg('São três opções de pagamento: PIX e boleto bancário, à vista, e cartão de crédito sem juros, com parcelas conforme o mês da compra. Sobre qual você quer saber?', {
        chips: [chip('PIX', 'pag_pix'), chip('Boleto', 'pag_boleto'), chip('Cartão de crédito', 'pag_cartao'), chip('Pagamento recusado', 'pag_recusado'), BACK],
      }),
    ],
  },
  pag_pix: {
    keywords: [],
    respond: ctx => [
      msg('O PIX é à vista. Ao escolher PIX no resumo, o QR Code e a chave copia-e-cola aparecem na tela com o tempo de validade. Se expirar, abra o pedido em Meus pedidos e use "Ver chave PIX" para gerar outra. Assim que o banco confirmar, o status muda para "Pagamento confirmado".', {
        actions: ctx.authenticated ? [go('Meus pedidos', '/v2/conta/pedidos')] : [],
        chips: [chip('Onde está meu pedido?', 'status'), HUMAN, BACK],
      }),
    ],
  },
  pag_boleto: {
    keywords: [],
    respond: ctx => [
      msg('O boleto é à vista. Enquanto o pedido estiver "Aguardando pagamento", a 2ª via fica em Meus pedidos, no botão "2ª via de boleto". Depois de pago, a confirmação depende da compensação bancária, que pode levar até 3 dias úteis.', {
        actions: ctx.authenticated ? [go('Meus pedidos', '/v2/conta/pedidos')] : [],
        chips: [chip('Onde está meu pedido?', 'status'), HUMAN, BACK],
      }),
    ],
  },
  pag_cartao: {
    keywords: [],
    respond: ctx => [
      // parcelas por mês: mesma tabela da API (CreditCardInstallmentHelper); a escola pode ter limite próprio
      msg('Cartão de crédito sem juros. O número de parcelas depende do mês da compra:\n' +
          '• novembro e dezembro: até 12 vezes\n' +
          '• janeiro: até 10 vezes\n' +
          '• fevereiro e março: até 8 vezes\n' +
          '• abril a outubro: até 3 vezes\n' +
          'Algumas escolas têm condição própria: as parcelas disponíveis aparecem na hora do pagamento. Também é possível pagar com dois cartões: no resumo, escolha "Dois cartões" e siga as etapas.', {
        actions: ctx.pathname === '/v2/steps/resumo' ? [spot('Mostrar formas de pagamento', '#v2-resumo-payment', '/v2/steps/resumo')] : [],
        chips: [chip('Pagamento recusado', 'pag_recusado'), HUMAN, BACK],
      }),
    ],
  },
  pag_recusado: {
    keywords: [],
    respond: () => [
      msg('A recusa vem da operadora do cartão (limite, dados divergentes ou análise antifraude), não do portal. Confira com o banco e tente novamente.', {
        chips: [chip('Ver meus pedidos', 'status'), HUMAN, BACK],
      }),
    ],
  },

  cancelamento: {
    keywords: ['cancel', 'desist', 'estorno', 'devolu', 'reembolso', 'troca', 'trocar', 'errado', 'arrependi', 'avaria', 'defeito'],
    respond: () => [
      msg('O que você precisa?', { chips: [chip('Quero cancelar a compra', 'cancelar'), chip('Livro errado ou com problema', 'troca'), BACK] }),
    ],
  },
  cancelar: {
    keywords: [],
    respond: () => [
      msg('Você pode solicitar o cancelamento em até 7 dias úteis, contados da data de recebimento do produto.'),
      msg('Para solicitar, abra um chamado. O cancelamento não é troca de produto.', {
        actions: [ticket()],
        chips: [chip('Livro errado ou com problema', 'troca'), BACK],
      }),
    ],
  },
  troca: {
    keywords: [],
    respond: () => [msg(EXCHANGE_RULE), msg(EXCHANGE_HOW, { chips: [HUMAN, BACK] })],
  },

  nota: {
    keywords: ['nota fiscal', 'nf', 'nota', 'danfe', 'fiscal'],
    respond: ctx => [
      msg('A nota fiscal fica no detalhe do pedido, no botão "Baixar NF", depois que o pedido é faturado. Se ainda estiver em processamento, o próprio portal avisa para aguardar.', {
        actions: ctx.authenticated ? [go('Meus pedidos', '/v2/conta/pedidos')] : [go('Entrar na minha conta', '/v2/conta/entrar')],
        chips: [chip('Onde está meu pedido?', 'status'), BACK],
      }),
    ],
  },

  cadastro: {
    keywords: ['senha', 'cadastro', 'cadastrar', 'login', 'entrar', 'email', 'e mail', 'conta', 'esqueci', 'acesso'],
    respond: () => [
      msg('Sobre o que é?', {
        chips: [chip('Esqueci minha senha', 'senha'), chip('Já tenho cadastro no site da SM', 'cadastro_sm'), chip('Não consigo me cadastrar ou acessar a conta', 'sem_acesso'), BACK],
      }),
    ],
  },
  senha: {
    keywords: [],
    respond: () => [
      msg('Na página de identificação do site, use o recurso "Esqueci minha senha" e digite o e-mail cadastrado. O sistema enviará para esse e-mail um link para você cadastrar uma nova senha. Se a sua conta foi criada pelo login com o Google e você nunca cadastrou uma senha, ao tentar entrar com e-mail e senha o sistema envia automaticamente o link para você criar a sua. Se preferir, continue entrando com o Google.', {
        actions: [go('Recuperar senha', '/v2/conta/esqueci-a-senha')],
        chips: [HUMAN, BACK],
      }),
    ],
  },
  cadastro_sm: {
    keywords: [],
    respond: () => [
      msg('Caso já tenha cadastro, não é necessário criar um novo. Você pode utilizar o mesmo usuário e senha. Aproveite para atualizar seus dados, caso seja necessário.', {
        chips: [chip('Esqueci minha senha', 'senha'), BACK],
      }),
    ],
  },

  // o canal de contato é o chamado; palavras de WhatsApp/telefone continuam
  // casando de propósito, para a pessoa receber a orientação em vez de "não entendi"
  // sem conta ou sem acesso a ela: o chamado da área logada não serve, então vai o formulário público
  sem_acesso: {
    keywords: ['nao consigo entrar', 'nao consigo acessar', 'nao consigo logar', 'nao consigo me cadastrar', 'nao consigo cadastrar', 'bloqueado', 'bloqueada', 'sem acesso'],
    respond: () => [
      msg('Primeiro, tente recuperar a senha em "Esqueci minha senha". Se ainda assim não conseguir se cadastrar ou acessar sua conta, preencha o formulário de atendimento e descreva o que acontece. Nossa equipe entrará em contato, se necessário. Nunca envie senha ou dados de cartão.', {
        actions: [go('Recuperar senha', '/v2/conta/esqueci-a-senha'), publicForm()],
        chips: [BACK],
      }),
    ],
  },

  humano: {
    keywords: ['atendente', 'humano', 'pessoa', 'falar', 'whatsapp', 'zap', 'telefone', 'ligar', 'contato', 'reclama', 'sac', 'chamado', 'ajuda de verdade'],
    respond: ctx => [
      msg(
        'Para falar com nosso time, abra um chamado. Descreva o que aconteceu e informe o número do pedido, se houver. Nossa equipe analisa e entra em contato com você, se necessário. Nunca envie senha ou dados de cartão.' +
          (ctx.authenticated ? '' : ' Você vai entrar na sua conta antes de abrir o chamado.'),
        { actions: [ticket()], chips: ctx.authenticated ? [BACK] : [chip('Não consigo acessar minha conta', 'sem_acesso'), BACK] }
      ),
    ],
  },
}

// Casamento de texto livre: soma de palavras-chave encontradas; empate fica com a
// primeira intenção declarada. Sem casamento → null (o componente trata o fallback).
export const matchIntent = text => {
  const t = ' ' + normalize(text) + ' '
  if (!t.trim()) return null
  let best = null
  let bestScore = 0
  Object.keys(INTENTS).forEach(id => {
    const score = (INTENTS[id].keywords || []).reduce((n, k) => (t.includes(' ' + k) || t.includes(k + ' ') || t.includes(k) ? n + 1 : n), 0)
    if (score > bestScore) {
      best = id
      bestScore = score
    }
  })
  return best
}

// chips contextuais por tela — aparecem junto com a saudação
export const contextChips = ctx => {
  const p = ctx.pathname || ''
  if (p === '/v2/steps/resumo')
    return [
      { label: 'Como aceito a entrega?', kind: 'spotlight', selector: '#v2-resumo-accept-box', path: p },
      { label: 'Formas de pagamento', kind: 'spotlight', selector: '#v2-resumo-payment', path: p },
    ]
  if (/^\/v2\/conta\/pedidos\/\d+/.test(p)) return [chip('Rastreio deste pedido', 'status'), chip('Nota fiscal', 'nota')]
  return []
}

export const greeting = ctx => {
  const name = ctx.user && (ctx.user.name || ctx.user.full_name)
  const hi = name ? `Oi, ${String(name).trim().split(/\s+/)[0]}!` : 'Oi!'
  return `${hi} Sou o assistente do SM Direto. Posso ajudar com pedidos, vouchers, pagamento e entrega. Sobre o que você quer falar?`
}
